import {
  AnchorButton as BlueprintAnchorButton,
  AnchorButtonProps as BlueprintAnchorButtonProps,
  PopoverPosition as BlueprintPopoverPosition,
} from '@blueprintjs/core';
import {
  Popover2 as BlueprintPopover2,
  Popover2Props as BlueprintPopover2Props,
} from '@blueprintjs/popover2';
import classNames from 'classnames';
import * as React from 'react';

import {
  InteractiveBadge,
  InteractiveBadgeProps,
} from 'bones/design-system/components/badge/InteractiveBadge';
import {
  Icon,
  IconComponentType,
  IconWrapperProps,
} from 'bones/design-system/components/icons/Icon';

const BADGE_SIZE_BY_MENU_SIZE: Record<
  MenuProps['size'],
  InteractiveBadgeProps['size']
> = {
  lg: 'md',
  md: 'md',
  sm: 'sm',
  xs: 'sm',
};

const ICON_SIZE_BY_MENU_SIZE: Record<
  MenuProps['size'],
  IconWrapperProps['size']
> = {
  lg: 'lg',
  md: 'md',
  sm: 'sm',
  xs: 'xs',
};

export type MenuProps = Pick<BlueprintAnchorButtonProps, 'disabled'> & {
  'data-testid'?: never;
  badgeProps?: Omit<
    InteractiveBadgeProps,
    'active' | 'disabled' | 'selected' | 'size' | 'variant'
  >;
  boundary?: BlueprintPopover2Props['boundary'];
  children?: React.ReactNode;
  className?: string;
  isOpen?: boolean;
  leftIcon?: IconComponentType;
  menu: React.ReactElement;
  onInteraction?: BlueprintPopover2Props['onInteraction'];
  popoverClassName?: string;
  position?: BlueprintPopoverPosition;
  rightIcon?: IconComponentType;
  size: 'lg' | 'md' | 'sm' | 'xs';
  testId?: string;
};

export const Menu: React.VFC<MenuProps> = ({
  badgeProps,
  boundary,
  children,
  className,
  disabled,
  isOpen: controlledIsOpen,
  leftIcon,
  menu,
  onInteraction,
  popoverClassName,
  position = BlueprintPopoverPosition.BOTTOM_RIGHT,
  rightIcon,
  size,
  testId,
}) => {
  const [isOpen, setIsOpen] = React.useState(controlledIsOpen ?? false);
  return (
    <div
      className={classNames(className, 'vb-menu', `vb-menu-size--${size}`, {
        'vb-menu-state--disabled': disabled,
      })}
    >
      <BlueprintPopover2
        boundary={boundary}
        content={menu}
        disabled={disabled}
        isOpen={controlledIsOpen ?? isOpen}
        minimal
        onInteraction={(nextIsOpen, evt) => {
          onInteraction?.(nextIsOpen, evt);
          if (controlledIsOpen === undefined) {
            setIsOpen(nextIsOpen);
          }
        }}
        popoverClassName={classNames(
          popoverClassName,
          'vb-menu-popover',
          `vb-popover-position--${position}`
        )}
        portalContainer={
          (process.env.NODE_ENV === 'test' &&
            document.getElementById('vet-bones-root')) ||
          // istanbul ignore next
          undefined
        }
        position={position}
      >
        <BlueprintAnchorButton
          active={isOpen}
          className={classNames('vb-menu-button', {
            'vb-menu-button--icon': !children,
          })}
          data-testid={testId}
          disabled={disabled}
        >
          {leftIcon ? (
            <Icon
              IconComponent={leftIcon}
              size={ICON_SIZE_BY_MENU_SIZE[size]}
            />
          ) : null}
          {children || badgeProps ? (
            <div className="vb-menu-button-text">
              {children}
              {badgeProps ? (
                <InteractiveBadge
                  active={isOpen}
                  disabled={disabled}
                  size={BADGE_SIZE_BY_MENU_SIZE[size]}
                  variant="light"
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...badgeProps}
                />
              ) : null}
            </div>
          ) : null}
          {rightIcon ? (
            <Icon
              IconComponent={rightIcon}
              size={ICON_SIZE_BY_MENU_SIZE[size]}
            />
          ) : null}
        </BlueprintAnchorButton>
      </BlueprintPopover2>
    </div>
  );
};
